/**
 * The record button and level meter.
 *
 * One control, three states: idle, starting, live. The pulsing ring and the
 * expanding level halo are separate elements so the pulse animation keeps its
 * own timing when the level changes.
 */

import { IconMic, IconStop, IconSparkle } from "./icons";

interface Props {
  live: boolean;
  busy: boolean;
  level: number;
  onClick: () => void;
  label: string;
  disabled?: boolean;
}

export function MicButton({ live, busy, level, onClick, label, disabled = false }: Props) {
  const normalized = Math.max(0, Math.min(1, level));

  return (
    <div className="row" style={{ gap: "var(--space-m)" }}>
      <button
        type="button"
        className="mic"
        data-live={live}
        onClick={onClick}
        disabled={disabled || busy}
        aria-label={label}
        aria-pressed={live}
        title={label}
        style={{ ["--level" as string]: normalized.toFixed(3) }}
      >
        <span className="mic__level" style={{ transform: `scale(${1 + normalized * 0.22})` }} />
        <span className="mic__ring" />
        {busy ? <span className="spinner" style={{ borderTopColor: "#fff" }} /> : live ? <IconStop size={26} /> : <IconMic size={28} />}
      </button>
      <div className="stack" style={{ gap: 2, minWidth: 0 }}>
        <span style={{ fontSize: "var(--step--1)", fontWeight: 600 }}>{busy ? "Connecting…" : live ? "Listening" : "Ready"}</span>
        <span className="dim" style={{ fontSize: "var(--step--2)" }}>{label}</span>
      </div>
    </div>
  );
}

/** 24-bar level meter. Cheap enough to run on every animation frame. */
export function LevelMeter({ level, bars = 24 }: { level: number; bars?: number }) {
  const normalized = Math.max(0, Math.min(1, level));
  return (
    <div className="row" style={{ gap: 2, alignItems: "flex-end", height: 22 }} aria-hidden="true">
      {Array.from({ length: bars }, (_, index) => {
        const threshold = index / bars;
        const active = normalized > threshold;
        // A gentle arc so the meter reads as a voice, not a progress bar.
        const height = 25 + Math.sin((index / bars) * Math.PI) * 75;
        return (
          <span
            key={index}
            style={{
              width: 2,
              height: `${active ? height : 18}%`,
              borderRadius: 1,
              background: active ? "var(--brand-grad)" : "var(--line-strong)",
              transition: "height 80ms var(--ease-out), background 120ms var(--ease-out)",
            }}
          />
        );
      })}
    </div>
  );
}

/** The strip shown under the mic when the browser recognizer is doing the work. */
export function BrowserAsrNotice({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <div className="row" style={{ gap: "var(--space-3xs)", color: "var(--text-dim)", fontSize: "var(--step--2)" }}>
      <IconSparkle size={12} />
      Instant on-device transcription is on. The server transcript is authoritative and lands in the log.
    </div>
  );
}
